import * as React from 'react';
import { Button, Popconfirm, Form, Input, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useUpsertFunnelAnalysis } from '@src/services/mutations';
import { isQueryError } from '@src/services/common';
import * as store from '../store';

const SavePopconfirm = () => {
  const { type, id } = store.useDashboardReadonlyData();
  const name = store.useDashboardName();
  const config = store.useDashboardConfig();
  const onSave = store.useOnSave();
  const { mutateAsync: upsertFunnelAnalysis } = useUpsertFunnelAnalysis([
    type === 'saved' ? id : undefined,
  ]);
  const [form] = Form.useForm<{ name: string }>();
  const configRef = React.useRef(config);

  React.useLayoutEffect(() => {
    configRef.current = config;
  }, [config]);

  const handleSave = React.useCallback(async () => {
    const values = await form.validateFields();
    await upsertFunnelAnalysis({
      ...configRef.current,
      name: values.name,
    })
      .then((savedId) => {
        onSave?.(savedId ?? id);
      })
      .catch((error) => {
        if (isQueryError(error, 40005)) {
          form.setFields([
            {
              name: 'name',
              errors: ['This name is already in use. Please choose another.'],
            },
          ]);
        } else {
          message.error((error as Error).message ?? 'Failed to save analysis.');
        }

        return Promise.reject(error as Error);
      });
  }, [form, upsertFunnelAnalysis, onSave, id]);

  return (
    <Popconfirm
      destroyOnHidden
      title={`${type === 'saved' ? 'Update' : 'Save'} Analysis`}
      placement="bottomLeft"
      icon={null}
      okText={type === 'saved' ? 'Update' : 'Save'}
      description={
        <Form
          form={form}
          layout="vertical"
          initialValues={{ name: type === 'saved' ? name : '' }}
          requiredMark={false}
        >
          <Form.Item
            name="name"
            label="Analysis Name"
            rules={[{ required: true, message: 'Please enter an analysis name' }]}
            style={{ marginBottom: 0, width: 400, padding: 16 }}
          >
            <Input placeholder="Enter an analysis name" maxLength={128} />
          </Form.Item>
        </Form>
      }
      onConfirm={handleSave}
      afterOpenChange={(visible) => {
        if (!visible) {
          form.resetFields();
        }
      }}
    >
      <Button icon={<SaveOutlined />} />
    </Popconfirm>
  );
};

export default React.memo(SavePopconfirm);
